import axios from 'axios';
import { APP_BASE_URL, GET_DEVICE_BY_CUSTOMER_ID, GET_LATEST_TELEMETRY, GET_SMPS_EVENT_LOGS, GET_SNMP_TRAPS, GET_TELEMETRY_HISTORY } from '../../../Common/EndPoints';
import {
  SMPSEventLogsApiResponse,
  GetSMPSEventLogsParams,
  TransformedSMPSResponse,
  SMPSTelemetryItem,
} from './smpsApiTypes';
import {
  transformTelemetryToSMPSData,
  parseEventLogData,
  parseMIBVoltage,
  parseMIBCurrent,
  transformTelemetryToSMPSDevice,
  parseSMPSDeviceData,
} from './smpsDataTransform';
import { SMPSAlert, SMPSAlarmType, VoltageHistoryRecord, SMPSEvent, SMPSDevice } from './smpsTypes';
import { DeviceTypeResponse } from '../../Kavach-dashboard/utils/kavachApiService';
import { NewTelemetryItem } from '../../Kavach-dashboard/utils/kavachApiTypes';

// Interface for device history params
interface GetDeviceHistoryParams {
  deviceId: string;
  page?: number;
  size?: number;
}

// Interface for device history response
export interface DeviceHistoryResponse {
  voltageHistory: VoltageHistoryRecord[];
  events: SMPSEvent[];
  device: SMPSDevice | null;
}

// Get auth token from localStorage
const getAuthToken = (): string => {
  try {
    const token = localStorage.getItem('token') || '';
    return token;
  } catch {
    return '';
  }
};

// Create axios instance with default config
const createAxiosConfig = () => ({
  headers: {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${getAuthToken()}`,
  },
});

export const getSMPSSystemId = () => {
  try {
    const systemsList = JSON.parse(localStorage.getItem('systems_list') || '[]');
    const smpsSystem = systemsList.find((sys: any) =>
      sys.code?.toUpperCase() === 'SMPS' ||
      sys.name?.toUpperCase().includes('SMPS')
    );
    return smpsSystem?.id || null;
  } catch {
    return null;
  }
}

export const getSMPSDeviceType = async (systemId: string, customerIds: string[]) => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('softwareIds', systemId);
    const url = `${APP_BASE_URL}${GET_DEVICE_BY_CUSTOMER_ID}?${queryParams.toString()}`;
    const response = await axios.post<DeviceTypeResponse>(url, customerIds, createAxiosConfig());
    return response.data.data[0].type || "IPS";
  } catch (error) {
    console.error('Error fetching SMPS device type:', error);
    return "IPS";
  }
}

/**
 * Get customer IDs from localStorage
 * Tries multiple keys used in the app
 */
export const getCustomerIds = (): string[] => {
  try {
    // Try different localStorage keys used in the app
    // 1. selected-customers (array of customer objects with id)
    const selectedCustomers = JSON.parse(localStorage.getItem('selected-customers') || '[]');
    if (selectedCustomers.length > 0) {
      const ids = selectedCustomers.map((c: any) => c.id?.toString() || c.customerId?.toString()).filter(Boolean);
      if (ids.length > 0) {
        return ids;
      }
    }

    // 2. customerIdList (comma-separated or JSON array)
    const customerIdList = localStorage.getItem('customerIdList');
    if (customerIdList) {
      try {
        const parsed = JSON.parse(customerIdList);
        const ids = Array.isArray(parsed) ? parsed.map(String) : [String(parsed)];
        if (ids.length > 0) {
          return ids;
        }
      } catch {
        // If not JSON, treat as comma-separated
        const ids = customerIdList.split(',').map(s => s.trim()).filter(Boolean);
        if (ids.length > 0) {
          return ids;
        }
      }
    }

    // 3. customerId (single customer ID)
    const customerId = localStorage.getItem('customerId');
    if (customerId) {
      return [customerId];
    }

    // 4. Original key (customerIds)
    const customerIds = JSON.parse(localStorage.getItem('customerIds') || '[]');
    if (customerIds.length > 0) {
      return customerIds;
    }

    return [];
  } catch {
    return [];
  }
};

// Map SNMP trap types to SMPSAlarmType
const mapTrapTypeToAlarmType = (trapType: string): SMPSAlarmType => {
  const upperType = trapType.toUpperCase();
  if (upperType.includes('LINK_DOWN') || upperType.includes('LINKDOWN')) return 'LINK_DOWN';
  if (upperType.includes('AC_FAIL') || upperType.includes('POWER')) return 'AC_FAIL';
  if (upperType.includes('BATTERY') || upperType.includes('BATT')) return 'BATTERY_LOW';
  if (upperType.includes('RECTIFIER') || upperType.includes('RECT')) return 'RECTIFIER_FAIL';
  if (upperType.includes('TEMP') || upperType.includes('HEAT')) return 'HIGH_TEMP';
  if (upperType.includes('LVD')) return 'LVD_ACTIVE';
  if (upperType.includes('VOLTAGE') && upperType.includes('HIGH')) return 'DC_OVERVOLTAGE';
  if (upperType.includes('VOLTAGE') && upperType.includes('LOW')) return 'DC_UNDERVOLTAGE';
  if (upperType.includes('COMM') || upperType.includes('COMMUNICATION')) return 'COMM_FAIL';
  return 'SNMP_TRAP';
};

/**
 * Fetch SMPS event logs for dashboard (latest status for all devices)
 * @param params - Query parameters (page, size)
 * @returns Transformed dashboard data with devices, alerts, stats
 */
export const fetchSMPSEventLogs = async (
  params: GetSMPSEventLogsParams = {},
  smpsSystemId?: string | null,
  customerIds?: string[]
): Promise<TransformedSMPSResponse> => {
  try {
    const systemId = smpsSystemId || getSMPSSystemId();
    const custIds = params.stn ? [params.stn] : customerIds || getCustomerIds();

    const deviceTypeCategory = await getSMPSDeviceType(systemId || "", custIds);
    const paramBody = {
      softwareId: systemId,
      customerIds: custIds,
      status: "ALL",
      deviceCategory: deviceTypeCategory,
    };

    const url = `${APP_BASE_URL}${GET_LATEST_TELEMETRY}`;
    const response = await axios.post(url, paramBody, createAxiosConfig());

    if (!response.data || !Array.isArray(response.data.data)) {
      return {
        devices: [],
        alerts: [],
        stats: {
          totalDevices: 0,
          healthy: 0,
          warning: 0,
          critical: 0,
          offline: 0,
          avgEfficiency: 0,
          totalLoad: 0,
        },
        stationStats: [],
        stations: ['ALL'],
        totalElements: 0,
        totalPages: 0,
        currentPage: 0,
      };
    }

    const telemetryItems: NewTelemetryItem[] = response.data.data;

    return transformTelemetryToSMPSData(telemetryItems, telemetryItems.length, 1, 0);
  } catch (error) {
    console.error('Error fetching SMPS event logs:', error);
    throw error;
  }
};

// export const fetchSMPSEventLogs = async (
//   params: GetSMPSEventLogsParams = {}
// ): Promise<TransformedSMPSResponse> => {
//   try {
//     const queryParams = new URLSearchParams();
//     queryParams.append('page', (params.page ?? 0).toString());
//     queryParams.append('size', (params.size ?? 200).toString());
//     queryParams.append('nameLike', params.stn ? `${params.stn}_SMPS` : 'SMPS');

//     const url = `${APP_BASE_URL}${GET_SMPS_EVENT_LOGS}?${queryParams.toString()}`;
//     const response = await axios.get<SMPSEventLogsApiResponse>(url, createAxiosConfig());

//     if (!response.data.success || !response.data.data) {
//       return {
//         devices: [],
//         alerts: [],
//         stats: {
//           totalDevices: 0,
//           healthy: 0,
//           warning: 0,
//           critical: 0,
//           offline: 0,
//           avgEfficiency: 0,
//           totalLoad: 0,
//         },
//         stationStats: [],
//         stations: ['ALL'],
//         totalElements: 0,
//         totalPages: 0,
//         currentPage: 0,
//       };
//     }

//     const { content, totalElements, totalPages, number } = response.data.data;
//     return transformSMPSEventLogs(content, totalElements, totalPages, number);
//   } catch (error) {
//     console.error('Error fetching SMPS event logs:', error);
//     throw error;
//   }
// };

interface GetTelemetryHistoryParams {
  page?: number;
  size?: number;
  deviceId: string;      // Filter by specific device
  fromDate?: number;      // Start timestamp (epoch ms)
  toDate?: number;        // End timestamp (epoch ms)
}
interface TelemetryHistoryApiResponse {
  error: {
    code: number
    message: string
    errors: any
  };
  success: boolean;
  data: {
    content: Array<{
      data: Array<{
        subGear: string
        dateTime: number
        values: {
          deviceIp: string
          totalResponseTimeMs: any
          monitoringType: string
          eventType: string
          deviceName: string
          results: string
          status: string
          PARENT_DEVICE: string
        }
        classification: any
      }>
    }>
    pageable: string
    totalElements: number
    totalPages: number
    last: boolean
    number: number
    sort: {
      sorted: boolean
      unsorted: boolean
      empty: boolean
    }
    size: number
    numberOfElements: number
    first: boolean
    empty: boolean
  }
}

export interface TelemetryHistoryTransformedResponse {
  content: Array<{
    subGear: string
    dateTime: number

    deviceIp: string
    totalResponseTimeMs: any
    monitoringType: string
    eventType: string
    deviceName: string
    results: {
      [key: string]: {
        oid: string;
        value: string,
        responseTimeMs: number
      }
    }
    status: string
    PARENT_DEVICE: string
    classification: any
  }>,
  totalElements: number,
  totalPages: number,
  currentPage: number,
}

/**
 * Fetch device history for a specific SMPS device
 * @param params - Query parameters (deviceId, page, size)
 * @returns Device history with voltage history, events, and device info
 */
export const fetchDeviceHistory = async (
  params: GetTelemetryHistoryParams
): Promise<DeviceHistoryResponse> => {
  try {
    const queryParams = new URLSearchParams();
    queryParams.append('page', (params.page ?? 1).toString());
    queryParams.append('pageSize', (params.size ?? 100).toString());
    queryParams.append('deviceId', params.deviceId.toString());
    queryParams.append('withCount', 'true');
    queryParams.append('isModelView', 'true');

    if (params.fromDate !== undefined) {
      queryParams.append('startTs', params.fromDate.valueOf().toString());
    }
    if (params.toDate !== undefined) {
      queryParams.append('endTs', params.toDate.valueOf().toString());
    }

    const url = `${APP_BASE_URL}${GET_TELEMETRY_HISTORY}?${queryParams.toString()}`;
    const response = await axios.get<TelemetryHistoryApiResponse>(url, createAxiosConfig());

    if (!response.data.success || !response.data.data || response.data.data.content.length === 0) {
      return {
        voltageHistory: [],
        events: [],
        device: null,
      };
    }

    const { content } = response.data.data;
    let _content = content.map(({ data }) => {
      const { dateTime, values, classification, subGear } = data[0];
      const { PARENT_DEVICE, deviceIp, deviceName, monitoringType, eventType, results, status, totalResponseTimeMs } = values;
      return {
        dateTime,
        classification,
        subGear,
        PARENT_DEVICE,
        deviceIp,
        deviceName,
        monitoringType,
        eventType,
        status,
        totalResponseTimeMs,
        results: JSON.parse(results)
      }
    });

    // Transform event logs to voltage history records
    const voltageHistory: VoltageHistoryRecord[] = _content
      .map((log): VoltageHistoryRecord | null => {
        const parsedData = log
        if (!parsedData || parsedData.status !== 'success') return null;

        const { results } = parsedData;

        // Extract voltage values from MIB format
        const dcVoltageRaw = results.system_voltage_mv?.value || results.DC_VOLTAGE?.value;
        const loadCurrentRaw = results.system_current_ma?.value || results.LOAD_CURRENT?.value;
        const battVoltageRaw = results.float_volt_mv?.value || results.BATTERY_VOLTAGE?.value;

        const dcVoltage = parseMIBVoltage(dcVoltageRaw);
        const loadCurrent = parseMIBCurrent(loadCurrentRaw);
        const batteryVoltage = parseMIBVoltage(battVoltageRaw);

        // Calculate efficiency
        const rectPowerW = parseInt(results.rect_total_power_w?.value || '0', 10);
        const dcPowerW = dcVoltage * loadCurrent;
        const efficiency = rectPowerW > 0 && dcPowerW > 0 ?
          Math.min(95, (dcPowerW / rectPowerW) * 100) :
          (dcVoltage > 0 && loadCurrent > 0 ? Math.min(95, 85 + (dcVoltage / 48) * 10) : 0);

        // Format timestamp
        const timestamp = new Date(log.dateTime).toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        }).replace(',', '');

        return {
          id: `vh-${log.dateTime}`,
          deviceId: params.deviceId.toString(),
          timestamp,
          dcVoltage: parseFloat(dcVoltage.toFixed(2)),
          loadCurrent: parseFloat(loadCurrent.toFixed(2)),
          batteryVoltage: parseFloat(batteryVoltage.toFixed(2)),
          efficiency: parseFloat(efficiency.toFixed(1)),
        };
      })
      .filter((record): record is VoltageHistoryRecord => record !== null);

    // Transform event logs to events (status changes, alarms)
    const events: SMPSEvent[] = _content
      .map((log): SMPSEvent => {
        const parsedData = log
        const eventType = parsedData?.eventType || 'SNMP_GET_SUCCESS';

        // Determine severity based on event type and system status
        let severity: 'critical' | 'warning' | 'info' = 'info';
        const systemStatus = parsedData?.results?.system_status?.value;
        if (systemStatus === '5' || systemStatus === '4') severity = 'critical';
        else if (systemStatus === '3') severity = 'warning';

        const timestamp = new Date(log.dateTime).toLocaleString('en-GB', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        }).replace(',', '');

        return {
          id: `ev-${log.dateTime}`,
          deviceId: params.deviceId.toString(),
          timestamp,
          eventType: eventType.includes('SUCCESS') ? 'status_change' : 'alarm',
          description: `${eventType} - ${parsedData?.deviceName}`,
          severity,
        };
      });

    // Get device info from first record
    const firstLog = _content[0]
    const device = parseSMPSDeviceData(firstLog, { ts: firstLog.dateTime, deviceId: params.deviceId } as any);
    // const device = transformedResponse.devices[0] || null;

    return {
      voltageHistory,
      events,
      device: device || null,
    };
  } catch (error) {
    console.error('Error fetching device history:', error);
    throw error;
  }
};
// export const fetchDeviceHistory = async (
//   params: GetDeviceHistoryParams
// ): Promise<DeviceHistoryResponse> => {
//   try {
//     const queryParams = new URLSearchParams();
//     queryParams.append('page', (params.page ?? 0).toString());
//     queryParams.append('size', (params.size ?? 100).toString());
//     queryParams.append('nameLike', 'SMPS');
//     queryParams.append('deviceId', params.deviceId);

//     const url = `${APP_BASE_URL}${GET_SMPS_EVENT_LOGS}?${queryParams.toString()}`;
//     const response = await axios.get<SMPSEventLogsApiResponse>(url, createAxiosConfig());

//     if (!response.data.success || !response.data.data || response.data.data.content.length === 0) {
//       return {
//         voltageHistory: [],
//         events: [],
//         device: null,
//       };
//     }

//     const { content } = response.data.data;

//     // Transform event logs to voltage history records
//     const voltageHistory: VoltageHistoryRecord[] = content
//       .filter((log: SMPSTelemetryItem) => log.deviceId.toString() === params.deviceId)
//       .map((log: SMPSTelemetryItem): VoltageHistoryRecord | null => {
//         const parsedData = parseEventLogData(log.data);
//         if (!parsedData || parsedData.status !== 'success') return null;

//         const { results } = parsedData;

//         // Extract voltage values from MIB format
//         const dcVoltageRaw = results.system_voltage_mv?.value || results.DC_VOLTAGE?.value;
//         const loadCurrentRaw = results.system_current_ma?.value || results.LOAD_CURRENT?.value;
//         const battVoltageRaw = results.float_volt_mv?.value || results.BATTERY_VOLTAGE?.value;

//         const dcVoltage = parseMIBVoltage(dcVoltageRaw);
//         const loadCurrent = parseMIBCurrent(loadCurrentRaw);
//         const batteryVoltage = parseMIBVoltage(battVoltageRaw);

//         // Calculate efficiency
//         const rectPowerW = parseInt(results.rect_total_power_w?.value || '0', 10);
//         const dcPowerW = dcVoltage * loadCurrent;
//         const efficiency = rectPowerW > 0 && dcPowerW > 0 ?
//           Math.min(95, (dcPowerW / rectPowerW) * 100) :
//           (dcVoltage > 0 && loadCurrent > 0 ? Math.min(95, 85 + (dcVoltage / 48) * 10) : 0);

//         // Format timestamp
//         const timestamp = new Date(log.createdAt).toLocaleString('en-GB', {
//           day: '2-digit',
//           month: '2-digit',
//           year: 'numeric',
//           hour: '2-digit',
//           minute: '2-digit',
//           second: '2-digit',
//           hour12: false,
//         }).replace(',', '');

//         return {
//           id: `vh-${log.id}`,
//           deviceId: log.deviceId.toString(),
//           timestamp,
//           dcVoltage: parseFloat(dcVoltage.toFixed(2)),
//           loadCurrent: parseFloat(loadCurrent.toFixed(2)),
//           batteryVoltage: parseFloat(batteryVoltage.toFixed(2)),
//           efficiency: parseFloat(efficiency.toFixed(1)),
//         };
//       })
//       .filter((record): record is VoltageHistoryRecord => record !== null);

//     // Transform event logs to events (status changes, alarms)
//     const events: SMPSEvent[] = content
//       .filter((log: SMPSTelemetryItem) => log.deviceId.toString() === params.deviceId)
//       .map((log: SMPSTelemetryItem): SMPSEvent => {
//         const parsedData = parseEventLogData(log.data);
//         const eventType = parsedData?.eventType || 'SNMP_GET_SUCCESS';

//         // Determine severity based on event type and system status
//         let severity: 'critical' | 'warning' | 'info' = 'info';
//         const systemStatus = parsedData?.results?.system_status?.value;
//         if (systemStatus === '5' || systemStatus === '4') severity = 'critical';
//         else if (systemStatus === '3') severity = 'warning';

//         const timestamp = new Date(log.createdAt).toLocaleString('en-GB', {
//           day: '2-digit',
//           month: '2-digit',
//           year: 'numeric',
//           hour: '2-digit',
//           minute: '2-digit',
//           second: '2-digit',
//           hour12: false,
//         }).replace(',', '');

//         return {
//           id: `ev-${log.id}`,
//           deviceId: log.deviceId.toString(),
//           timestamp,
//           eventType: eventType.includes('SUCCESS') ? 'status_change' : 'alarm',
//           description: `${eventType} - ${parsedData?.deviceName || log.name}`,
//           severity,
//         };
//       });

//     // Get device info from first record
//     const firstLog = content.find((log: SMPSTelemetryItem) => log.deviceId.toString() === params.deviceId);
//     const transformedResponse = transformTelemetryToSMPSData(
//       firstLog ? [firstLog] : [],
//       1,
//       1,
//       0
//     );
//     const device = transformedResponse.devices[0] || null;

//     return {
//       voltageHistory,
//       events,
//       device,
//     };
//   } catch (error) {
//     console.error('Error fetching device history:', error);
//     throw error;
//   }
// };
